/**
 * Phase 7 API client (additive) — auctions, transactions, disputes, risk.
 */
import { apiClient } from "./api";

export const auctionsAPI = {
  list: (params = {}) => apiClient.get("/platform/auctions", { params }).then((r) => r.data),
  mine: (params = {}) => apiClient.get("/platform/auctions/mine", { params }).then((r) => r.data),
  get: (id) => apiClient.get(`/platform/auctions/${id}`).then((r) => r.data),
  create: (payload) => apiClient.post("/platform/auctions", payload).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/platform/auctions/${id}`, patch).then((r) => r.data),
  cancel: (id, reason) => apiClient.post(`/platform/auctions/${id}/cancel`, {}, { params: reason ? { reason } : {} }).then((r) => r.data),
  bid: (id, payload) => apiClient.post(`/platform/auctions/${id}/bids`, payload).then((r) => r.data),
  bids: (id, params = {}) => apiClient.get(`/platform/auctions/${id}/bids`, { params }).then((r) => r.data),
  cancelBid: (bid_id) => apiClient.post(`/platform/auctions/bids/${bid_id}/cancel`).then((r) => r.data),
  buyNow: (id) => apiClient.post(`/platform/auctions/${id}/buy-now`).then((r) => r.data),
  analytics: (bid) => apiClient.get(`/platform/auctions/analytics/business/${bid}`).then((r) => r.data),
};

export const txAPI = {
  mine: (params = {}) => apiClient.get("/platform/transactions/mine", { params }).then((r) => r.data),
  business: (bid, params = {}) => apiClient.get(`/platform/transactions/business/${bid}`, { params }).then((r) => r.data),
  get: (id) => apiClient.get(`/platform/transactions/${id}`).then((r) => r.data),
  reserve: (id) => apiClient.post(`/platform/transactions/${id}/reserve`).then((r) => r.data),
  pay: (id, payment_intent_id) => apiClient.post(`/platform/transactions/${id}/pay`, {}, { params: payment_intent_id ? { payment_intent_id } : {} }).then((r) => r.data),
  processing: (id) => apiClient.post(`/platform/transactions/${id}/processing`).then((r) => r.data),
  confirm: (id) => apiClient.post(`/platform/transactions/${id}/confirm`).then((r) => r.data),
  cancel: (id, reason) => apiClient.post(`/platform/transactions/${id}/cancel`, { reason }).then((r) => r.data),
  refund: (id, reason) => apiClient.post(`/platform/transactions/${id}/refund`, { reason }).then((r) => r.data),
};

export const disputesV2API = {
  open: (payload) => apiClient.post("/platform/disputes", payload).then((r) => r.data),
  mine: (params = {}) => apiClient.get("/platform/disputes/mine", { params }).then((r) => r.data),
  admin: (params = {}) => apiClient.get("/platform/disputes/admin", { params }).then((r) => r.data),
  get: (id) => apiClient.get(`/platform/disputes/${id}`).then((r) => r.data),
  respond: (id, text) => apiClient.post(`/platform/disputes/${id}/response`, { text }).then((r) => r.data),
  addEvidence: (id, payload) => apiClient.post(`/platform/disputes/${id}/evidence`, payload).then((r) => r.data),
  listEvidence: (id) => apiClient.get(`/platform/disputes/${id}/evidence`).then((r) => r.data),
  adminNote: (id, text) => apiClient.post(`/platform/disputes/${id}/admin/note`, { text }).then((r) => r.data),
  resolve: (id, body) => apiClient.post(`/platform/disputes/${id}/admin/resolve`, body).then((r) => r.data),
  withdraw: (id) => apiClient.post(`/platform/disputes/${id}/withdraw`).then((r) => r.data),
};

export const riskAPI = {
  list: (params = {}) => apiClient.get("/platform/risk/flags", { params }).then((r) => r.data),
  review: (id, body) => apiClient.post(`/platform/risk/flags/${id}/review`, body).then((r) => r.data),
  scanAuction: (id) => apiClient.post(`/platform/risk/scan/auction/${id}`).then((r) => r.data),
  scanAll: () => apiClient.post("/platform/risk/scan/all").then((r) => r.data),
};

/* Enums */
export const AUCTION_STATUS_LABELS = {
  draft:     { ar: "مسودة",       en: "Draft"     },
  scheduled: { ar: "مجدول",       en: "Scheduled" },
  live:      { ar: "مباشر",       en: "Live"      },
  extended:  { ar: "تم التمديد",  en: "Extended"  },
  ended:     { ar: "منتهي",       en: "Ended"     },
  sold:      { ar: "مبيع",        en: "Sold"      },
  unsold:    { ar: "لم يُبع",     en: "Unsold"    },
  canceled:  { ar: "ملغى",        en: "Canceled"  },
};

export const TRANSACTION_STATUS_LABELS = {
  pending:    { ar: "قيد الانتظار",  en: "Pending",    tone: "amber" },
  reserved:   { ar: "محجوز",         en: "Reserved",   tone: "amber" },
  paid:       { ar: "مدفوع",         en: "Paid",       tone: "blue"  },
  processing: { ar: "قيد التنفيذ",   en: "Processing", tone: "blue"  },
  completed:  { ar: "مكتمل",         en: "Completed",  tone: "green" },
  canceled:   { ar: "ملغى",          en: "Canceled",   tone: "gray"  },
  refunded:   { ar: "مسترد",         en: "Refunded",   tone: "gray"  },
  disputed:   { ar: "متنازع عليه",   en: "Disputed",   tone: "red"   },
};

export const DISPUTE_KIND_LABELS = {
  item_not_delivered:      { ar: "لم يُسلّم",           en: "Item not delivered" },
  item_not_as_described:   { ar: "لا يطابق الوصف",      en: "Not as described"   },
  payment_issue:           { ar: "مشكلة دفع",           en: "Payment issue"      },
  auction_fraud:           { ar: "احتيال في المزاد",   en: "Auction fraud"      },
  seller_no_response:      { ar: "لا يوجد رد من البائع", en: "Seller no response" },
  buyer_no_response:       { ar: "لا يوجد رد من المشتري", en: "Buyer no response" },
  other:                   { ar: "أخرى",                en: "Other"              },
};

export const DISPUTE_STATUS_LABELS = {
  opened:            { ar: "مفتوح",           en: "Opened",           tone: "amber" },
  responded:         { ar: "بانتظار الحل",    en: "Awaiting review",  tone: "blue"  },
  under_review:      { ar: "قيد المراجعة",    en: "Under review",     tone: "blue"  },
  resolved_buyer:    { ar: "لصالح المشتري",   en: "Resolved (buyer)", tone: "green" },
  resolved_seller:   { ar: "لصالح البائع",    en: "Resolved (seller)", tone: "green" },
  resolved_split:    { ar: "تسوية مشتركة",    en: "Split resolution", tone: "green" },
  withdrawn:         { ar: "مسحوب",           en: "Withdrawn",        tone: "gray"  },
};

export function formatCountdown(endsAtISO) {
  const ends = new Date(endsAtISO);
  const now = new Date();
  const diff = Math.max(0, ends - now);
  const s = Math.floor(diff / 1000);
  const days = Math.floor(s / 86400);
  const hrs = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}ي ${hrs}س`;
  if (hrs > 0) return `${hrs}س ${mins}د`;
  if (mins > 0) return `${mins}د ${String(secs).padStart(2, "0")}ث`;
  return `${secs}ث`;
}
